<template>
    <div class="main">
        <div id="Nav" class="left">
            <div class="back"><button @click="goBack()" class="button icon fastic-circle-left" :aria-label="localizer.get('App.Common.PageBack')"></button></div>
            <div class="content">
                <h1 class="maintitle s60">{{ localizer.get("Account.Common.Register_Title") }}</h1>
                <div class="desc">{{ localizer.get("Account.Common.Register_Description") }}</div>
            </div>
            <app-footer></app-footer>
        </div>
        <main id="Main" class="right">
            <div class="logo" data-color="cyan50">
                <a :href="routes.Home"><img src="/images/logo.svg" width="125" height="35" :alt="localizer.get('App.Common.Beghelli')" /></a>
            </div>
            <div v-if="options" @keydown.enter.prevent.stop="submitForm()" class="content">
                <div class="intro">
                    <h2 class="maintitle s36">{{ localizer.get("Account.ExternalRegister.CompleteProfile") }}</h2>
                </div>
                <div ref="form" class="form">
                    <fieldset>
                        <div v-if="options.isUserName" class="item">
                            <label for="UserName">{{ localizer.get("Account.Common.UserName") }}</label>
                            <input type="text" id="UserName" v-model.lazy="formData.userName" :class="[{ 'input-validation-error': formHelper.isControlValidationError('UserName', validationErrors) }, 'input w300']" />
                        </div>
                        <div class="item">
                            <label for="Email">{{ localizer.get("App.Form.Email") }}</label>
                            <input type="email" id="Email" v-model.lazy="formData.email" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Email', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-if="options.isEmailCheck" class="item">
                            <label for="EmailCheck">{{ localizer.get("Account.Common.EmailCheck") }}</label>
                            <input type="email" id="EmailCheck" v-model.lazy="formData.emailCheck" :class="[{ 'input-validation-error': formHelper.isControlValidationError('EmailCheck', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-if="isEmailInUse()" class="desc small">{{ localizer.get("Account.Common.Email_InUse_RetrieveText") }} <a :href="routes.Account_User_PasswordRemind">{{ localizer.get("Account.Common.Email_InUse_RetrieveLink") }}</a></div>
                        <div class="item" data-label="top">
                            <label for="ProfileSegment">{{ localizer.get("App.Common.AddFunctionalities") }}</label>
                            <select id="ProfileSegment" v-model="formData.profileSegment" :class="[{ 'input-validation-error': formHelper.isControlValidationError('ProfileSegment', validationErrors) }, 'input w460']">
                                <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                <option v-for="(profileSegment, index) in profileSegments" :value="profileSegment.value" :key="index">{{ profileSegment.title }}</option>
                            </select>
                        </div>
                    </fieldset>
                    <fieldset>
                        <div class="item" data-type="list">
                            <div class="checkbox">
                                <input type="checkbox" id="IsNewsletterSubscription" v-model="formData.isNewsletterSubscription">
                                <label for="IsNewsletterSubscription">{{ localizer.get('App.Form.NewsletterSubscription') }}</label>
                            </div>
                        </div>
                        <div class="desc small" v-html="localizer.get('App.Form.PrivacyA', routes.UrlPrivacyPolicy)"></div>
                        <div v-if="validationErrors.length !== 0" class="validation-summary-errors">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="cta"><button :class="[{ 'loading': isLoading }, 'button']" @click="submitForm()"><template v-if="!isLoading">{{ localizer.get("Account.Common.Button_Next") }}</template></button></div>
                    </fieldset>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";

const props = defineProps<{ userName: string; email: string; returnUrl: string }>();

const localizer = Localizer;
const formHelper = FormHelper;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const options = ref<any>(null);
const profileSegments = ref<any[]>([]);
const formData = ref<any>({ profileSegment: null, isNewsletterSubscription: false });
const validationErrors = ref<any[]>([]);

onBeforeMount(async () => {
    options.value = (await Axios.post(routes.Account_User_GetOptions, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
    profileSegments.value = options.value.profileSegments;
    formData.value.userName = props.userName ?? null;
    formData.value.email = props.email ?? null;
});

async function submitForm() {
    FormHelper.removeFocus();
    validationErrors.value = [];
    isLoading.value = true;
    try {
        let result = (await Axios.post(routes.Account_External_ExternalRegister, formData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
        isSuccess.value = true;
        window.location.href = result.redirectUrl ? result.redirectUrl : (props.returnUrl ? props.returnUrl : "/");
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }
    isLoading.value = false;
}

function isEmailInUse(): boolean {
    return (validationErrors.value.length !== 0 && validationErrors.value.filter((x: any) => x.message === localizer.get("Account.Common.Email_InUse")).length > 0);
}

function goBack() {
    props.returnUrl ? window.location.href = props.returnUrl : history.back();
}
</script>